import { useState } from "react";
import {
	MagnifyingGlassIcon,
	PlusIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/20/solid";
import { Trash } from "lucide-react";

function RoleDropdown({ value, onChange }) {
	return (
		<select
			value={value}
			onChange={(e) => onChange(e.target.value)}
			className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 focus:outline-none"
		>
			<option value="Viewer">Viewer</option>
			<option value="Editor">Editor</option>
			<option value="Full Access">Full Access</option>
		</select>
	);
}

export default function TeamMemberManagement() {
	const [isInviteOpen, setIsInviteOpen] = useState(false);
	const [emails, setEmails] = useState("");

	const [members, setMembers] = useState([
		{
			id: 1,
			name: "Shilpa G",
			email: "shgopal@gmail.com",
			status: "Active",
			role: "Owner",
			isInvited: false,
			avatar:
				"https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=64&h=64",
		},
		{
			id: 2,
			name: "Invited Member",
			email: "ajaykpanduri@gmail.com",
			status: "Pending",
			role: "Member",
			accessLevel: "Viewer",
			isInvited: true,
		},
	]);

	const handleInvite = () => {
		console.log("Inviting:", emails);
		setEmails("");
		setIsInviteOpen(false);
	};

	const updateAccess = (id, level) => {
		setMembers((prev) =>
			prev.map((m) => (m.id === id ? { ...m, accessLevel: level } : m)),
		);
	};

	const resendInvite = (email) => {
		console.log("Resend invite to:", email);
	};

	const deleteInvite = (id) => {
		setMembers((prev) => prev.filter((m) => m.id !== id));
	};

	return (
		<div className="min-h-screen bg-gray-50 p-6">
			<div className="mx-auto max-w-6xl">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">Your Team</h1>
						<p className="mt-1 text-sm text-gray-500">
							Manage your team in one place
						</p>
					</div>
					<button
						onClick={() => setIsInviteOpen(true)}
						className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
					>
						<PlusIcon className="h-4 w-4" />
						Invite Member
					</button>
				</div>

				{/* Card */}
				<div className="mt-6 rounded-2xl bg-white shadow">
					<div className="border-b border-gray-200 px-6 py-4">
						<h2 className="text-sm font-medium text-gray-900">
							Team Directory
						</h2>
					</div>

					{/* Search */}
					<div className="px-6 py-4">
						<div className="relative max-w-sm">
							<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Search"
								className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
							/>
						</div>
					</div>

					{/* Table */}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
										Name
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
										Status
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
										Role
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
										Email
									</th>
								</tr>
							</thead>

							<tbody className="divide-y divide-gray-100 bg-white">
								{members.map((member) => (
									<tr key={member.id} className="bg-blue-50/40">
										{/* Name */}
										<td className="px-6 py-4">
											<div className="flex items-center gap-3">
												{member.avatar ? (
													<img
														src={member.avatar}
														alt=""
														className="h-9 w-9 rounded-full"
													/>
												) : (
													<div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700">
														{member.name[0]}
													</div>
												)}
												<span className="text-sm font-medium text-gray-900">
													{member.name}
												</span>
											</div>
										</td>

										{/* Status */}
										<td className="px-6 py-4">
											{member.status === "Active" ? (
												<span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
													<span className="h-2 w-2 rounded-full bg-green-500" />
													Active
												</span>
											) : (
												<span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700">
													<span className="h-2 w-2 rounded-full bg-yellow-500" />
													Pending
												</span>
											)}
										</td>

										{/* Role */}
										<td className="px-6 py-4">
											{member.role === "Owner" ? (
												<span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
													Owner
												</span>
											) : (
												<div className="flex items-center gap-2">
													<span className="text-xs font-medium text-blue-700">
														Member
													</span>
													<RoleDropdown
														value={member.accessLevel}
														onChange={(v) => updateAccess(member.id, v)}
													/>
												</div>
											)}
										</td>

										{/* Email + Actions */}
										<td className="px-6 py-4 text-sm text-gray-700">
											<div className="flex items-center justify-between gap-4">
												<span>{member.email}</span>

												{member.isInvited && (
													<div className="flex items-center gap-3">
														<button
															onClick={() => resendInvite(member.email)}
															className="text-sm text-gray-500 hover:text-black"
														>
															Re-send
														</button>
														<button
															onClick={() => deleteInvite(member.id)}
															className="rounded-lg bg-red-50 p-2 text-red-500 hover:bg-red-100"
														>
															<Trash className="h-4 w-4" />
														</button>
													</div>
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Invite Modal */}
			{isInviteOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-semibold text-gray-900">
								Invite New Team Member
							</h3>
							<button
								onClick={() => setIsInviteOpen(false)}
								className="rounded-lg p-1 hover:bg-gray-100"
							>
								<XMarkIcon className="h-5 w-5 text-gray-500" />
							</button>
						</div>

						<div className="mt-4 flex gap-3">
							<input
								type="text"
								value={emails}
								onChange={(e) => setEmails(e.target.value)}
								placeholder="Emails, comma separated"
								className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
							/>
							<button
								onClick={handleInvite}
								className="rounded-lg bg-black px-5 py-2 text-sm font-medium text-white hover:bg-gray-800"
							>
								Invite
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
